// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { decodeMappings, MalformedMapError, type DecodedMappings, type Segment } from './vlq';
import type { LocationOutcome } from '../types';

/**
 * Reverse lookup: a byte offset in the generated file -> the original (source, line, column).
 *
 * TWO THINGS THE SPEC GOT WRONG HERE, BOTH MEASURED.
 *
 * 1. SOURCEMAP COLUMNS ARE UTF-16 CODE UNITS, NOT BYTES. ECMA-426, verbatim: columns are "computed
 *    as UTF-16 code units for JavaScript and CSS source maps, and as byte indices in the binary
 *    content ... for WebAssembly." On a real vite@8.2.1 build, an 824-byte / 794-UTF-16-unit file
 *    put the needle at byte offset 766 = UTF-16 offset 736 — a delta of THIRTY from a handful of
 *    emoji and one `é`. And Vite 8 emits non-ASCII literally by default, so this is the common case,
 *    not an edge case. Using the byte offset as a column shifts every finding after the first
 *    non-ASCII character by a constant, which looks internally consistent while being uniformly
 *    wrong.
 *
 * 2. COLUMNS ARE PER GENERATED LINE, NOT ABSOLUTE.
 *
 * And one the spec did not mention at all, which produces the most convincing wrong answer this
 * tool could give: UNMAPPED REGIONS. Measured against `source-map`, columns 5 and 669 of a real Vite
 * bundle (the modulepreload polyfill) return `{source: null, line: null, column: null}`. A 1-field
 * segment EXPLICITLY marks generated code as unmapped. Inheriting the previous 4-field segment
 * yields a real filename and a real line number that are entirely fictional.
 */

export interface SourceMapV3 {
  version?: number;
  file?: string;
  sourceRoot?: string;
  sources?: (string | null)[];
  sourcesContent?: (string | null)[];
  names?: string[];
  mappings?: string;
  sections?: unknown[];
  ignoreList?: number[];
}

export interface PreparedMap {
  readonly decoded: DecodedMappings;
  readonly sources: (string | null)[];
  readonly sourcesContent: (string | null)[];
  readonly names: string[];
  readonly sourceRoot: string;
}

export function prepareMap(map: SourceMapV3): PreparedMap {
  const sources = map.sources ?? [];
  const names = map.names ?? [];
  const decoded = decodeMappings(map.mappings ?? '', sources.length, names.length);
  return {
    decoded,
    sources,
    sourcesContent: map.sourcesContent ?? [],
    names,
    sourceRoot: map.sourceRoot ?? '',
  };
}

/**
 * Line-start index over the RAW BYTES of a generated file. `Int32Array` because a 200 MB bundle has
 * a lot of lines and this is built once per file.
 */
export function lineStarts(bytes: Uint8Array): Int32Array {
  const starts: number[] = [0];
  for (let i = 0; i < bytes.length; i++) if (bytes[i] === 0x0a) starts.push(i + 1);
  return Int32Array.from(starts);
}

/** Greatest index whose value is <= target. */
function upperBound(arr: Int32Array, target: number): number {
  let lo = 0;
  let hi = arr.length - 1;
  let ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid]! <= target) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

const dec = new TextDecoder('utf-8');

/**
 * Convert a byte offset into `(generatedLine, utf16Column)`.
 *
 * Only the CONTAINING generated line is decoded — a minified bundle is one line, but that line is
 * the whole file, so we slice to the target rather than decoding past it.
 */
export function byteOffsetToLineColumn(
  bytes: Uint8Array,
  starts: Int32Array,
  byteOffset: number,
): { generatedLine: number; utf16Column: number } {
  const lineIndex = upperBound(starts, byteOffset);
  const lineStart = starts[lineIndex]!;
  const prefix = dec.decode(bytes.subarray(lineStart, byteOffset));
  return { generatedLine: lineIndex, utf16Column: prefix.length };
}

export interface OriginalLocation {
  readonly outcome: LocationOutcome;
  readonly source?: string;
  /** 1-based, because editors are. Raw VLQ lines are 0-based. */
  readonly originalLine?: number;
  readonly originalColumn?: number;
  readonly sourceIndex?: number;
}

/**
 * Greatest-lower-bound search for the segment owning a generated column.
 *
 * THREE EXPLICIT OUTCOMES, and the difference between them is the difference between a useful tool
 * and a confident liar:
 *   - `mapped`      — a 4- or 5-field segment owns this column.
 *   - `unmapped`    — the column falls before the first segment on the line, OR lands on a 1-field
 *                     segment. NEVER snap to the nearest source: a secret in an unmapped region is
 *                     still a real leak, it just has no provenance.
 *   - `approximate` — the column falls after the last segment on the line. That segment owns it, but
 *                     its true extent is unknown, so the caller must say so.
 */
export function originalFor(prepared: PreparedMap, generatedLine: number, utf16Column: number): OriginalLocation {
  const line = prepared.decoded.lines[generatedLine];
  if (!line || line.length === 0) return { outcome: 'unmapped' };

  let lo = 0;
  let hi = line.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (line[mid]!.generatedColumn <= utf16Column) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (found < 0) return { outcome: 'unmapped' };

  const seg: Segment = line[found]!;
  if (seg.sourceIndex === undefined) return { outcome: 'unmapped' };

  const isLast = found === line.length - 1;
  const beyond = isLast && utf16Column > seg.generatedColumn;
  const raw = prepared.sources[seg.sourceIndex];
  return {
    outcome: beyond ? 'approximate' : 'mapped',
    source: raw === null || raw === undefined ? undefined : raw,
    sourceIndex: seg.sourceIndex,
    originalLine: seg.originalLine! + 1,
    originalColumn: seg.originalColumn!,
  };
}

/**
 * Classify a finding once its location is known.
 *
 * THE SPEC SAID the sourcemap tells you where your secret came from. FOR THE PRIMARY CASE IT DOES
 * NOT. A build-time inlined value has no original source position for the literal, because the
 * literal did not exist in the source. Measured three ways:
 *   (a) `import.meta.env.VITE_STRIPE_KEY` — the mapping resolves to `src/main.js:1:6`, whose
 *       original line is `const key = import.meta.env.VITE_STRIPE_KEY;`. The leaked literal appears
 *       nowhere in it.
 *   (b) with object-spread reads, it maps to the read site.
 *   (c) with the minifier inlining consts and deleting the source line, it mapped to
 *       `src/live.js:6:10` = `return [a, b].join("|")` — A DIFFERENT FILE, confidently wrong.
 *
 * So there are two finding classes with different wording and different remediation advice, and the
 * primary attribution for a build-inlined finding is the `.env` key plus public-prefix analysis, not
 * the sourcemap.
 */
export function classifyFinding(valueInSourcesContent: boolean, hasMap: boolean): 'literal-in-source' | 'build-inlined' | 'no-map' {
  if (!hasMap) return 'no-map';
  return valueInSourcesContent ? 'literal-in-source' : 'build-inlined';
}

export { MalformedMapError };
